import { z } from 'zod';
import { roundFractionToCents } from '../money-math';
import { localizedTextSchema, microsStringSchema, quoteCalcGroupKeySchema } from './quote-calculation.schema';

/**
 * Bausteine des Angebots-Designers (Visual Editor, 2026-10-12), die nicht aus der
 * Kalkulation kommen: Überschriften, Texte, Zwischensummen und Preisermittlungen auf
 * der Angebotsseite, freie Texte und eine Preisübersicht bei den Optionen, eigene
 * Spezifikationen (auch aus dem Artikelstamm) und eigene Konditionen. Alle Felder sind
 * optional — Angebote von vorher bleiben gültig; `reconcileQuoteOffer` ergänzt, was fehlt.
 *
 * Geld bleibt `bigint` (Mikro). Prozente sind Zahlen mit höchstens zwei Stellen und
 * werden in Basispunkte gewandelt; jede gedruckte Zeile ist auf Cent gerundet, die
 * Summen sind Summen der gedruckten Zeilen (CLAUDE.md §6: gedruckt = gerechnet).
 */

const idSchema = z.string().trim().min(1).max(64);
const percentSchema = z.number().finite().min(0).max(100);

/** Rechenzeile: Rabatt/Zuschlag in % der Summe darüber oder fester Betrag (negativ = Gutschrift). */
export const offerCalcRowSchema = z.object({
  id: idSchema,
  op: z.enum(['discount', 'surcharge', 'amount']),
  label: localizedTextSchema,
  percent: percentSchema.nullable(),
  amountMicros: microsStringSchema.nullable(),
});
export type OfferCalcRow = z.infer<typeof offerCalcRowSchema>;

/** Reihenfolge der Angebotsseite: Gruppen der Kalkulation und Bausteine dazwischen. */
export const offerFlowItemSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('group'), key: quoteCalcGroupKeySchema, breakBefore: z.boolean().optional() }),
  z.object({ kind: z.literal('heading'), id: idSchema, title: localizedTextSchema, subtitle: localizedTextSchema, breakBefore: z.boolean().optional() }),
  z.object({ kind: z.literal('text'), id: idSchema, text: localizedTextSchema, breakBefore: z.boolean().optional() }),
  /** Summe der Gruppen seit der letzten Zwischensumme bzw. Preisermittlung. */
  z.object({ kind: z.literal('subtotal'), id: idSchema, label: localizedTextSchema, breakBefore: z.boolean().optional() }),
  /** Summe aller Gruppen darüber, dann die Zeilen in Reihenfolge, dann das Ergebnis. */
  z.object({
    kind: z.literal('calc'),
    id: idSchema,
    title: localizedTextSchema,
    baseLabel: localizedTextSchema,
    result: localizedTextSchema,
    rows: z.array(offerCalcRowSchema).max(20),
    breakBefore: z.boolean().optional(),
  }),
]);
export type OfferFlowItem = z.infer<typeof offerFlowItemSchema>;
export type OfferFlowBlock = Exclude<OfferFlowItem, { kind: 'group' }>;

/** Freie Zeile bei den technischen Daten (nach den Feldern aus der Kalkulation). */
export const offerTechRowSchema = z.object({ id: idSchema, label: localizedTextSchema, value: z.string().max(200) });
export type OfferTechRow = z.infer<typeof offerTechRowSchema>;

/** Preisübersicht bei den Optionen: Angebotspreis + eingeblendete Optionen, dann die Zeilen. */
export const offerPriceSummarySchema = z.object({
  kind: z.literal('priceSummary'),
  id: idSchema,
  isVisible: z.boolean(),
  title: localizedTextSchema,
  baseLabel: localizedTextSchema,
  result: localizedTextSchema,
  rows: z.array(offerCalcRowSchema).max(20),
  breakBefore: z.boolean().optional(),
});
export type OfferPriceSummary = z.infer<typeof offerPriceSummarySchema>;

const specRowSchema = z.object({ label: z.string().max(1000), value: z.string().max(2000), isTotal: z.boolean().optional() });
const specRowsByLanguageSchema = z.object({ de: z.array(specRowSchema).max(60), en: z.array(specRowSchema).max(60) });

/** Eigener Eintrag auf den Spezifikationsseiten (Karte, auch aus dem Artikelstamm, Überschrift, Text). */
export const offerSpecExtraSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('card'),
    /** Satz der Seiten: Anlagen-ID bzw. „project". */
    setKey: idSchema,
    /** Geladen aus dem Artikelstamm (Soft-Ref); null = von Hand angelegt. */
    articleId: z.string().uuid().nullable(),
    title: localizedTextSchema,
    rows: specRowsByLanguageSchema,
  }),
  z.object({ kind: z.literal('heading'), setKey: idSchema, title: localizedTextSchema }),
  z.object({ kind: z.literal('text'), setKey: idSchema, text: localizedTextSchema }),
]);
export type OfferSpecExtra = z.infer<typeof offerSpecExtraSchema>;

/** Eigene Kondition; ohne Bezeichnung = freier Text über die ganze Breite. */
export const offerCustomTermSchema = z.object({ id: idSchema, label: localizedTextSchema, value: localizedTextSchema });
export type OfferCustomTerm = z.infer<typeof offerCustomTermSchema>;

export const offerLogoLayoutSchema = z.object({
  /** Breite des Logos auf dem Blatt in mm. */
  widthMm: z.number().finite().min(20).max(80),
  align: z.enum(['right', 'left']),
});
export type OfferLogoLayout = z.infer<typeof offerLogoLayoutSchema>;
export const DEFAULT_OFFER_LOGO: OfferLogoLayout = { widthMm: 44, align: 'right' };

// ---------- Rechnen ----------

/** Prozent → Basispunkte (zwei Nachkommastellen). */
const basisPoints = (percent: number | null): bigint => BigInt(Math.round((percent ?? 0) * 100));

export interface OfferCalcRowResult {
  id: string;
  /** Summe vor dieser Zeile (Grundlage für Prozent). */
  beforeMicros: bigint;
  /** Betrag der Zeile, auf Cent; Rabatt negativ. */
  amountMicros: bigint;
}

/**
 * Zeilen einer Preisermittlung bzw. Preisübersicht ab `baseMicros` (bereits auf Cent):
 * Prozent immer von der laufenden Summe darüber, jede Zeile auf Cent gerundet.
 */
export function computeOfferCalcRows(baseMicros: bigint, rows: readonly OfferCalcRow[]): { rows: OfferCalcRowResult[]; resultMicros: bigint } {
  let running = baseMicros;
  const results = rows.map((row): OfferCalcRowResult => {
    const before = running;
    const share = roundFractionToCents(before * basisPoints(row.percent), 10_000n);
    const amount = row.op === 'discount' ? -share : row.op === 'surcharge' ? share : BigInt(row.amountMicros ?? '0');
    running += amount;
    return { id: row.id, beforeMicros: before, amountMicros: amount };
  });
  return { rows: results, resultMicros: running };
}

/** Gruppen-Schlüssel in der Reihenfolge der Angebotsseite. */
export const flowGroupKeys = (flow: readonly OfferFlowItem[]): string[] => flow.flatMap((item) => (item.kind === 'group' ? [item.key] : []));
